import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSubmitInviteRequest } from '@/hooks/useInviteRequest';

interface FormData {
  email: string;
}

export function InviteRequestForm() {
  const [submitted, setSubmitted] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const submitRequest = useSubmitInviteRequest();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const result = await submitRequest.mutateAsync({
        email: data.email,
      });
      setResponseMessage(result.message);
      setSubmitted(true);
      reset();
    } catch (error) {
      setResponseMessage(
        error instanceof Error ? error.message : 'An error occurred. Please try again.'
      );
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8 text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
            <svg
              className="h-8 w-8 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              role="img"
              aria-label="Success"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
        <h3 className="mb-2 text-xl font-semibold text-white">You're on the list!</h3>
        <p className="text-gray-400">{responseMessage}</p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="mt-6 text-indigo-400 hover:text-indigo-300"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8">
      <div className="mb-6 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-3 py-1 text-xs text-indigo-300">
          Coming Soon
        </div>
        <h2 className="text-2xl font-bold text-white">Get Early Access</h2>
        <p className="mt-2 text-gray-400">
          Be among the first to create your own AI-powered podcasts. We'll notify you when we're ready.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300">
            Email
          </label>
          <input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Please enter a valid email address',
              },
            })}
            className="mt-1 block w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
          )}
        </div>

        {responseMessage && !submitted && (
          <div className="rounded-md bg-red-900/50 p-3 text-sm text-red-300">
            {responseMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-md bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-2 font-medium text-white hover:from-indigo-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Get Notified'}
        </button>
      </form>
    </div>
  );
}
